import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getInvite, acceptInvite, setupAndAcceptInvite } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Menu, CheckCircle, AlertCircle, Users } from 'lucide-react';

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, login } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // For new users
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // For existing users who aren't logged in yet
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    async function fetchInvite() {
      try {
        const data = await getInvite(token);
        setInvite(data);
        setLoginEmail(data.email);
      } catch (err) {
        const detail = err.response?.data?.detail;
        setError(detail || 'Invite not found or has expired.');
      } finally {
        setLoading(false);
      }
    }
    fetchInvite();
  }, [token]);

  const handleAccept = async () => {
    setSubmitting(true);
    setError('');
    try {
      await acceptInvite(token);
      setSuccess(true);
      setTimeout(() => navigate('/menus'), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Failed to accept invite.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoginThenAccept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const result = await login(loginEmail, loginPassword);
    if (!result.success) {
      setError(result.error || 'Login failed');
      setSubmitting(false);
      return;
    }
    try {
      await acceptInvite(token);
      setSuccess(true);
      setTimeout(() => navigate('/menus'), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Failed to accept invite.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (password.length < 8 || !/\d/.test(password) || !/[a-zA-Z]/.test(password)) {
      setError('Password must be at least 8 characters and include a letter and a number');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const data = await setupAndAcceptInvite(token, { name, password });
      // Store auth
      localStorage.setItem('line_rich_menu_token', data.access_token);
      localStorage.setItem('line_rich_menu_user', JSON.stringify(data.user));
      setSuccess(true);
      setTimeout(() => window.location.href = '/menus', 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Failed to create account.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
        <CardHeader className="space-y-2 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center">
              <Menu className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-zinc-100">
            {error ? 'Invalid Invitation' : 'Workspace Invitation'}
          </CardTitle>
          {invite && !error && (
            <CardDescription className="text-zinc-400">
              <span className="font-medium text-zinc-300">{invite.inviterName}</span> invited you to join{' '}
              <span className="font-medium text-zinc-300">{invite.workspaceName}</span>
            </CardDescription>
          )}
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="bg-red-500/10 border-red-500/20 mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle className="w-12 h-12 text-emerald-500" />
              <p className="text-zinc-100 font-medium">You've joined {invite?.workspaceName}!</p>
              <p className="text-zinc-400 text-sm">Redirecting to menus...</p>
            </div>
          )}

          {!success && invite && (
            <>
              <div className="flex items-center gap-2 mb-6 p-3 rounded-lg bg-zinc-800/60 border border-zinc-700">
                <Users className="w-4 h-4 text-emerald-500 shrink-0" />
                <div className="text-sm">
                  <span className="text-zinc-400">Joining as </span>
                  <span className="text-zinc-200 font-medium capitalize">{invite.role}</span>
                  <span className="text-zinc-400"> · {invite.email}</span>
                </div>
              </div>

              {/* Existing user, already logged in with matching email */}
              {invite.userExists && isAuthenticated && user?.email === invite.email && (
                <Button
                  onClick={handleAccept}
                  disabled={submitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Accept &amp; Join Workspace
                </Button>
              )}

              {/* Existing user, not logged in or wrong account */}
              {invite.userExists && (!isAuthenticated || user?.email !== invite.email) && (
                <>
                  {!showLogin ? (
                    <div className="space-y-3">
                      <p className="text-zinc-400 text-sm text-center">
                        An account with <span className="text-zinc-200">{invite.email}</span> already exists.
                        Sign in to accept the invitation.
                      </p>
                      <Button
                        onClick={() => setShowLogin(true)}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        Sign In to Accept
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleLoginThenAccept} className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Email</Label>
                        <Input
                          type="email"
                          value={loginEmail}
                          readOnly
                          className="bg-zinc-800 border-zinc-700 text-zinc-400"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Password</Label>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500"
                          autoFocus
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Sign In &amp; Accept
                      </Button>
                    </form>
                  )}
                </>
              )}

              {/* New user — create account */}
              {!invite.userExists && (
                <form onSubmit={handleSetup} className="space-y-4">
                  <p className="text-zinc-400 text-sm text-center mb-2">
                    Create your account to join the workspace.
                  </p>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Full Name</Label>
                    <Input
                      type="text"
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500"
                      required
                      autoFocus
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Password</Label>
                    <Input
                      type="password"
                      placeholder="8+ characters, letters and numbers"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Confirm Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Create Account &amp; Join
                  </Button>
                </form>
              )}
            </>
          )}

          {error && !invite && (
            <div className="text-center mt-2">
              <Button
                variant="ghost"
                className="text-zinc-400 hover:text-zinc-200"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
